// src/app/helpers/ga4UserSegmentation.js
// Purpose: Map user profile data to GA4 user properties

import { setGA4Userproperties } from './ga4'

/**
 * Extract campus from user profile
 * Maps locationCdDesc or campus flags to campus names
 */
const extractCampus = (userProfile) => {
  if (!userProfile) return 'Unknown'

  // Check location description first
  const locationDesc = userProfile.locationCdDesc || ''
  if (locationDesc.includes('New Brunswick')) return 'New Brunswick'
  if (locationDesc.includes('Newark') || locationDesc.includes('NWK')) {
    return 'Newark'
  }
  if (locationDesc.includes('Camden') || locationDesc.includes('CMD')) {
    return 'Camden'
  }

  // Fallback to campus flags
  if (userProfile.isNB) return 'New Brunswick'
  if (userProfile.isNW) return 'Newark'
  if (userProfile.isCM) return 'Camden'

  return 'Unknown'
}

/**
 * Extract user type from user profile
 * Determines if user is Student, Faculty, Staff, Visitor, or combination
 */
const extractUserType = (userProfile) => {
  if (!userProfile) return 'Visitor'

  const types = []

  // Check employee status first (has priority)
  if (userProfile.isFaculty) {
    types.push('Faculty')
  } else if (userProfile.isStaff) {
    types.push('Staff')
  } else if (userProfile.isEmployee) {
    types.push('Employee')
  }

  // Check student status
  if (
    userProfile.isStudent ||
    userProfile.isRuStudent ||
    userProfile.isRbhsStudent
  ) {
    types.push('Student')
  }

  // Return combined type or default
  if (types.length > 0) {
    return types.join('+') // e.g., "Staff+Student"
  }

  return 'Visitor'
}

/**
 * Extract affiliation from user profile
 * Can be department or school
 */
const extractAffiliation = (userProfile) => {
  if (!userProfile) return 'Unknown'

  // Prefer department for employees
  if (userProfile.department) {
    return userProfile.department
  }

  // Use affiliation field
  if (userProfile.affiliation) {
    return userProfile.affiliation
  }

  return 'Unknown'
}

/**
 * Get user segmentation data to add to trackEvent calls
 * Returns object with campus, userType, affiliation
 */
export const getUserSegmentationData = (userProfile) => {
  if (!userProfile) {
    return {
      campus: 'Unknown',
      userType: 'Visitor',
      affiliation: 'Unknown',
    }
  }

  return {
    campus: extractCampus(userProfile),
    userType: extractUserType(userProfile),
    affiliation: extractAffiliation(userProfile),
  }
}

/**
 * Initialize GA4 user properties based on user profile
 * Call this after user login or when profile is loaded
 */
export const initializeGA4UserProperties = (userProfile) => {
  try {
    if (!userProfile) {
      console.log('No user profile available for GA4 initialization')
      return
    }

    const campus = extractCampus(userProfile)
    const userType = extractUserType(userProfile)
    const affiliation = extractAffiliation(userProfile)

    if (process.env.NODE_ENV === 'development') {
      console.log('GA4 User Properties:', {
        campus,
        userType,
        affiliation,
      })
    }

    // Send to GA4
    setGA4Userproperties({
      campus,
      userType,
      affiliation,
    })
  } catch (error) {
    console.error('Error initializing GA4 user properties:', error)
  }
}

/**
 * Update GA4 user properties if profile changes
 * (e.g., after switching roles/campuses)
 */
export const updateGA4UserProperties = (userProfile) => {
  initializeGA4UserProperties(userProfile)
}
